import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  BrewGuideDetails,
  SuperHero,
  useSlideOverDialogContext,
} from "@bluebottlecoffee/design-system/components";
import { useCart } from "@chordcommerce/react-autonomy";
import type { Product } from "@bluebottlecoffee/design-system/components/lib/types";
import { BrewGuideStepsProjection } from "../../../../lib/sanity/recipe-menu-queries";
import { DereferencedRecipeMenu } from "../../../../lib/transformers/recipe-menu";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import useVariantPrice from "../../../../lib/chord/hooks/components/use-variant-price";
import { isFeatureEnabled } from "../../../../lib/utils/is-feature-enabled";
import useVariantAvailability from "../../../../lib/chord/hooks/components/use-variant-availability";
import { toBrewGuideDetailsProps } from "../../../../lib/transformers/detail-page-overview";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import sanityClient from "../../../../lib/sanity-client";
import { CartBasePropsWithSlug } from "../../../../lib/sanity/shared";
import { filters } from "../../../../lib/sanity/utils";
import { getDefaultStaticPropsMiniCartWithSlug } from "../../../../lib/utils";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  BrewGuide as BrewGuideSanitySchema,
  BrewGuideRecipe,
  BrewGuideRecipeShareLinks,
} from "../../../../lib/sanity-schema";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import renderModule from "../../../../lib/render-module";
import localization from "../../../../localization";
import { REGION } from "../../../../lib/i18n";
import { brewGuidesRecipes } from "../../../../lib/sanity/brew-guides-recipes-queries";
import { trackEvent } from "../../../../lib/segment/track-event";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";

export type BrewGuideSchema = Omit<BrewGuideSanitySchema, "brewGuideRecipe"> & {
  brewGuideRecipe: Omit<BrewGuideRecipe, "brewGuideRecipeSteps"> & {
    brewGuideRecipeSteps: BrewGuideStepsProjection | DereferencedRecipeMenu;
    shareLinks: BrewGuideRecipeShareLinks;
  };
};

interface PageData extends CartBasePropsWithSlug {
  brewGuide: BrewGuideSchema;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } =
    await getDefaultStaticPropsMiniCartWithSlug(context);
  const { slug, lang, region } = defaultProps;

  const brewGuide = await sanityClient().fetch<BrewGuideSchema>(
    brewGuidesRecipes({
      lang,
      slug,
    }),
  );

  await resolveTabbedCollections(brewGuide?.brewModules, { lang, region });

  return {
    props: {
      brewGuide,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient().fetch<string[]>(
    `*[
        ${filters.whereType("brewGuide")} &&
        ${filters.excludeDrafts}
    ].slug.current`,
  );

  const params = localization.languages(REGION).flatMap((lang) =>
    slugs.map((slug) => ({
      params: {
        region: REGION,
        lang: lang.code,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

const BrewGuidePage: FunctionComponent<PageData> = ({
  brewGuide,
  ...layoutWrapperProps
}) => {
  const {
    region,
    lang,
    giftCardFormCopy,
    subscribableProducts,
    cart,
    navAndCartInfoBanner,
    productRecs,
    aria,
    appPlayLinks,
  } = layoutWrapperProps;

  const { setActiveContentType } = useSlideOverDialogContext();
  const { addToCart: chordAddToCart } = useCart();

  const useCartProps = {
    useVariantPrice,
    useVariantAvailability,
    addToCart: async (product: Product) => {
      const { sku } = product.variants[0];
      await chordAddToCart({ sku, quantity: 1 });
      setActiveContentType("minicart");
      trackEvent("brew_guide_add_to_cart", {
        quantity: 1,
        name: product.name,
        sku,
      });
    },
  };

  const miniCartDialog = (
    <MiniCartDialog
      productRecs={productRecs}
      giftCardFormCopy={giftCardFormCopy}
      miniCartCopy={cart}
      navAndCartInfoBanner={navAndCartInfoBanner}
      region={region}
      lang={lang}
      subscribableProducts={
        isFeatureEnabled(
          process.env.NEXT_PUBLIC_CART_SUBSCRIPTION_TOGGLE_ENABLED,
        )
          ? subscribableProducts
          : []
      }
    />
  );

  return (
    <>
      <CanonicalHead
        region={region}
        lang={lang}
        slug={`brew-guides/${brewGuide.slug.current}`}
      />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,
          pageTitle: brewGuide.title[lang],
          metaTags: brewGuide.metaTags,
          slideOutContent: [miniCartDialog],
          openMiniCartPanel: Boolean(miniCartDialog),
        }}
        isTransparent
      >
        {brewGuide.hero?.length > 0 && (
          <SuperHero
            {...toSuperHeroProps(brewGuide.hero, aria, region, lang)}
          />
        )}

        <BrewGuideDetails
          {...toBrewGuideDetailsProps({
            brewGuide,
            useCartProps,
            lang,
            region,
          })}
        />

        {brewGuide.brewModules?.length > 0 &&
          brewGuide.brewModules.map((module) =>
            renderModule({
              module,
              region,
              lang,
              appPlayLinks,
              aria,
            }),
          )}
      </LayoutWrapper>
    </>
  );
};

export default BrewGuidePage;
